/**
 * «Логотипы по брендам» (28.09.2026). Бренд = название организации до первой
 * запятой: «Kaspi.kz, банкомат» и «Kaspi.kz, постамат» — один бренд Kaspi.kz,
 * а «Halyk Insurance» и «Halyk» — разные. Логотип, загруженный здесь, встаёт
 * на все точки бренда сразу; у отдельной организации его можно перебить в
 * «Справочнике». Приложение подхватывает изменения в течение часа.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Input, Table, Tabs, Tag, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { directoryApi, BrandRow } from '../../api/directory';
import LogoCell from './LogoCell';
import LogoCandidates from './LogoCandidates';

const { Title, Text } = Typography;

const BrandLogos: React.FC = () => {
  const [rows, setRows] = useState<BrandRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await directoryApi.brands(q);
      setRows(res.data.items);
    } catch {
      message.error('Не удалось загрузить бренды');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { void load(); }, [load]);

  const columns = [
    {
      title: 'Логотип',
      key: 'logo',
      width: 150,
      render: (_: unknown, r: BrandRow) => (
        <LogoCell
          url={r.logo_url}
          name={r.title}
          uploadLabel={`Загрузить логотип на все точки: ${r.total}`}
          removeConfirm={`Убрать логотип у всех точек «${r.title}» (${r.total})?`}
          onUpload={async (file) => { await directoryApi.setBrandLogo(r.brand, file); await load(); }}
          onRemove={async () => { await directoryApi.clearBrandLogo(r.brand); await load(); }}
        />
      ),
    },
    { title: 'Бренд', dataIndex: 'title', key: 'title' },
    { title: 'Точек', dataIndex: 'total', key: 'total', width: 90 },
    {
      title: 'С логотипом',
      key: 'with_logo',
      width: 130,
      render: (_: unknown, r: BrandRow) =>
        r.with_logo === 0 ? <Tag>нет</Tag> : r.with_logo === r.total ? <Tag color="green">все</Tag> : <Tag color="orange">{r.with_logo} из {r.total}</Tag>,
    },
    {
      title: 'Instagram',
      dataIndex: 'instagram',
      key: 'instagram',
      render: (v?: string | null) =>
        v ? <a href={v} target="_blank" rel="noreferrer">{v.replace(/^https?:\/\/(www\.)?instagram\.com\//, '@')}</a> : <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>Логотипы по брендам</Title>
      <Text type="secondary">
        Логотип бренда встаёт на все его точки (отделения, банкоматы, терминалы). В приложении появится в течение часа.
      </Text>
      <Tabs
        style={{ marginTop: 8 }}
        items={[
          {
            key: 'brands',
            label: 'Бренды',
            children: (
              <>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Найти бренд: Kaspi, Halyk, Magnum…"
                style={{ maxWidth: 360, margin: '16px 0' }}
                onPressEnter={(e) => setQ((e.target as HTMLInputElement).value)}
                onChange={(e) => { if (!e.target.value) setQ(''); }}
              />
              <Table rowKey="brand" dataSource={rows} columns={columns} loading={loading} pagination={{ pageSize: 50 }} size="middle" />
              </>
            ),
          },
          { key: 'candidates', label: 'Кандидаты', children: <LogoCandidates /> },
        ]}
      />
    </div>
  );
};

export default BrandLogos;
